import { PRICE_FIELDS, formatPrice, parseText } from './productPricingHelpers';

// Light, spreadsheet-style highlights. Fixed rather than taken from the MUI palette, whose
// warning/success shades are too dark to read black text on.
export const EDITED_CELL_COLOR = '#FFECB3';
// The rest of a row with an edit gets a lighter shade of the same amber.
export const EDITED_ROW_COLOR = '#FFF8E1';
export const ADDED_ROW_COLOR = '#E3F4E8';
// A cell the last save was refused for.
export const ERROR_CELL_COLOR = '#FDECEA';
// Its red outline, drawn inside the cell so it doesn't fight the grid's own cell borders and focus
// ring.
export const ERROR_CELL_OUTLINE = 'inset 0 0 0 2px #D32F2F';

// Every field an admin can edit (all but NDC), for telling whether a row has any edit.
export const EDITABLE_FIELDS = [
  'name',
  'strength',
  'dgDrugName',
  'code',
  'coldShipped',
  ...PRICE_FIELDS.map(({ field }) => field),
];

// Column headers by field, for naming the cells in a failed save's problem list.
export const FIELD_HEADERS = {
  ndc: 'NDC',
  name: 'Name',
  strength: 'Strength',
  dgDrugName: 'DG_Drug_Name',
  code: 'Code',
  coldShipped: 'Cold_Shipped',
  ...Object.fromEntries(PRICE_FIELDS.map(({ field, headerName }) => [field, headerName])),
};

const PRICE_FIELD_KEYS = new Set(PRICE_FIELDS.map(({ field }) => field));

// Money is right-aligned in the cells only. AG Grid's 'rightAligned' column type would also
// mirror the header, putting its menu and filter icons on the left of the label.
export const getTextAlign = (field) => {
  if (PRICE_FIELD_KEYS.has(field)) return 'right';
  if (field === 'coldShipped') return 'center';
  return 'left';
};

// Text filters by default (price columns use number filters): with AG Grid Enterprise a bare
// `filter: true` is the Set Filter, whose floating filter is a read-only summary of the picked
// values rather than a box to type in. The floating filter's button, which only opens the same
// filter in a popup, is hidden to keep the filter row as plain inputs.
//
// Column sizes, pinning and sort use AG Grid's initial* attributes throughout: the column defs
// are re-set after every edit (see handleCellValueChanged), and plain width/sort/pinned would
// undo any resizing or re-sorting the admin has done each time.
export const baseColDef = {
  filter: 'agTextColumnFilter',
  floatingFilter: true,
  suppressFloatingFilterButton: true,
  sortable: true,
  resizable: true,
  initialWidth: 120,
};

// The grid's columns, in the price sheet's order. cellStyle comes from the page, since it reads
// the edit and save state.
export const buildColumnDefs = (cellStyle) => [
  // NDC identifies the row, so it's never editable; new NDCs come in through Add Product.
  {
    field: 'ndc',
    headerName: 'NDC',
    editable: false,
    initialPinned: 'left',
    initialWidth: 130,
    cellStyle,
  },
  {
    field: 'name',
    headerName: 'Name',
    // Pinned beside NDC so the product stays in view while scrolling across the fees.
    initialPinned: 'left',
    initialWidth: 240,
    // Opens sorted by Name; one sort column at a time (suppressMultiSort on the grid).
    initialSort: 'asc',
    valueParser: parseText,
    cellStyle,
  },
  {
    field: 'strength',
    headerName: 'Strength',
    initialWidth: 220,
    valueParser: parseText,
    cellStyle,
  },
  {
    field: 'dgDrugName',
    headerName: 'DG_Drug_Name',
    initialFlex: 1,
    minWidth: 360,
    valueParser: parseText,
    cellStyle,
  },
  {
    field: 'code',
    headerName: 'Code',
    initialWidth: 200,
    valueParser: parseText,
    cellStyle,
  },
  {
    field: 'coldShipped',
    headerName: 'Cold_Shipped',
    initialWidth: 170,
    // The price sheet's Y / N flag, kept as-is rather than turned into a boolean.
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: { values: ['Y', 'N'] },
    cellStyle,
  },
  ...PRICE_FIELDS.map(({ field, headerName, width }) => ({
    field,
    headerName,
    initialWidth: width,
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    cellEditor: 'agNumberCellEditor',
    // No min: a negative entry is accepted and shown red (see getCellProblem), not blocked.
    cellEditorParams: { precision: 4 },
    valueFormatter: ({ value }) => formatPrice(value),
    // Exported as plain numbers (1191.61, not "$1,191.61") so they stay numbers in a spreadsheet.
    useValueFormatterForExport: false,
    cellStyle,
  })),
];
